import { createApi } from "@reduxjs/toolkit/query/react";
import customFetchBase from "./customFetchBase";
import { getQueryParams } from "utils/getQueryParams";
import {
  IGenericResponse,
  IPaginatedList,
  IPaginationQuery,
} from "Interfaces/PaginatedList";
import { ICompany } from "Models/Company";
import { CompaniesFilters } from "Interfaces/CompaniesFilters";
import { ICompanyToken } from "Models/CompanyToken";

export const companyApi = createApi({
  reducerPath: "companyApi",
  baseQuery: customFetchBase,
  tagTypes: ["Company"],
  keepUnusedDataFor: 300,  // Keep cached data for 5 minutes globally
  refetchOnReconnect: true, // Refetch data when reconnected to the network
  refetchOnFocus: true, // Refetch data when the window is focused again
  endpoints: (builder) => ({
    getCompanies: builder.query<IPaginatedList<ICompany>, IPaginationQuery<CompaniesFilters>>({
      query(queryFilters) {

        return {generateApiKey: builder.mutation<{ apiKey: string }, void>({
      query() {
        return {
          url: "company/generate-apikey",
          method: "POST",
          credentials: "include",
        };
      },
      transformResponse: (result: { data: { apiKey: string } }) => {
        return result.data;
      },
    }),
          url: getQueryParams(`company`, queryFilters),
          credentials: "include",
        };
      },
      transformResponse: (results: IGenericResponse<IPaginatedList<ICompany>>) =>
        results.data,
      providesTags: ["Company"],
    }),
    getAllCompanies: builder.query<ICompany[], void>({
      query() {
        return {
          url: "company/get/all",
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: () => [{ type: "Company" }],
      transformResponse: (results: { data: ICompany[] }) => {
        return results.data;
      },
    }),
    // Get single company

    getACompany: builder.query<ICompany, string>({
      query(companyId) {
        
        return {
          url: "company/" + companyId,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: ["Company"],
      transformResponse: (result: { data: ICompany }) => {
        return result.data;
      },
    }),

    // Post company

    postCompany: builder.mutation<ICompany, FormData>({
      query(newCompany) {
        return {
          url: "company",
          method: "POST",
          body: newCompany,
          credentials: "include",
        };
      },
      invalidatesTags: ["Company"],
      transformResponse: (result: { data:  ICompany }) => {
        return result.data;
      },
    }),

    // Update company

    updateCompany: builder.mutation<
      ICompany,
      { id: string; newsToUpdate: FormData }
    >({
      query({ id: companyId, newsToUpdate: updatedCompany }) {
        return {
          url: "company/" + companyId,
          method: "PUT",
          body: updatedCompany,
          credentials: "include",
        };
      },
      invalidatesTags: ["Company"],
      transformResponse: (result: { data: ICompany }) => {
        return result.data;
      },
    }),

    // Delete company

    deleteCompany: builder.mutation<ICompany, string>({
      query(companyId) {
        return {
          url: "company/" + companyId,
          method: "DELETE",
          credentials: "include",
        };
      },
      invalidatesTags: ["Company"],
      transformResponse: (result: { data: ICompany }) => {
        return result.data;
      },
    }),
    generateApiKey: builder.mutation<{ token: string }, {companyId: string}>({
      query({ companyId }) {
        return {
          url: "company/generate-token",
          method: "POST",
          body: {
            companyId,
          },
          credentials: "include",
        };
      },
      transformResponse: (result: { data: { token: string } }) => {
        return result.data;
      },
    }),
    revokeApiKey: builder.mutation<{ message: string }, { companyId: string } >({
      query: ({ companyId }) => ({
        url: `company/revoke-token/${companyId}`, 
        method: "POST",
        credentials: "include",
      }),
      transformResponse: (response: { status: string; message: string }) => {
        return { message: response.message };
      },
    }),

    getCompanyApiKey: builder.query<ICompanyToken, string | void>({
      query: (companyId) => {
        const queryParam = companyId ? `?companyId=${companyId}` : '';
        return {
          url: `company/get-company-token/${queryParam}`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: ["Company"],
      transformResponse: (result: { data: ICompanyToken }) => {
        return result.data;
      },
    }),    
  }),
});

export const {
  useGetACompanyQuery,
  useGetCompaniesQuery,
  usePostCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useGetAllCompaniesQuery,
  useGenerateApiKeyMutation,
  useRevokeApiKeyMutation,
  useGetCompanyApiKeyQuery,
} = companyApi;
